// Pricing table for active loon products. Guards + render tree only; all
// selection, analytics, and checkout flow lives in useProductPricingTable.

import React from 'react';
import { CircleAlertIcon } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyContent,
  EmptyMedia,
  EmptyTitle,
} from '../ui/empty';
import { Spinner } from '../ui/spinner';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from '../ui/item';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Separator } from '../ui/separator';
import { TransitionMessage, SITE } from '../transitionMessage';
import { cn } from '@/utilities/cn';
import { useProductPricingTable } from '../../hooks';
import { useLoonProducts } from '../../transport/cache';
import type { AnalyticsSurface } from '../../types/analytics';
import type { ProductDocument } from '@airhead-games/shared/types';
import {
  formatProductPrice,
  getExtraLoonsPercent,
  getProductLoonsGranted,
  getProductPresentation,
} from '@airhead-games/shared/utilities';

type PricingTableLayoutMode = 'auto' | 'singleColumn';

interface PricingTableProps {
  analyticsSurface: AnalyticsSurface;
  layoutMode?: PricingTableLayoutMode;
  onCancel?: () => void;
  /** Called when a saved-method quick purchase succeeds (e.g. to close a dialog). */
  onPurchaseSuccess?: () => void;
}

interface PricingPackView {
  stripePriceId: string;
  asset: string;
  loonsLabel: string;
  extraLoonsPercent: number | null;
  price: string;
  pricingBadge?: 'Most Popular' | 'BEST VALUE';
  gradientClasses: string;
}

/**
 * Maps pack size to a subtle color-wash gradient. Ordered warm → green so
 * the most valuable pack reads as the most on-brand green. Kept frontend-side
 * so shared/ stays UI-framework-agnostic.
 */
const PACK_GRADIENT_BY_SIZE: Record<number, string> = {
  10: 'bg-gradient-to-br from-red-500/25 to-card',
  25: 'bg-gradient-to-br from-orange-500/25 to-card',
  50: 'bg-gradient-to-br from-yellow-500/25 to-card',
  100: 'bg-gradient-to-br from-green-500/25 to-card',
};

const DEFAULT_PACK_GRADIENT = 'bg-gradient-to-br from-green-500/25 to-card';

const getPackGradient = (loonsGranted: number | null): string => {
  if (!loonsGranted) {
    return DEFAULT_PACK_GRADIENT;
  }
  return PACK_GRADIENT_BY_SIZE[loonsGranted] ?? DEFAULT_PACK_GRADIENT;
};

const buildPackView = (
  product: ProductDocument,
  baselinePricePerLoon: number | null
): PricingPackView => {
  const loonsGranted = getProductLoonsGranted(product);
  const { asset, pricingBadge } = getProductPresentation(product);
  const { price } = formatProductPrice(product);
  return {
    stripePriceId: product.stripePriceId,
    asset,
    loonsLabel: loonsGranted
      ? `${loonsGranted.toLocaleString()} Loons`
      : product.name,
    extraLoonsPercent: getExtraLoonsPercent(product, baselinePricePerLoon),
    price,
    pricingBadge,
    gradientClasses: getPackGradient(loonsGranted),
  };
};

const PricingTable: React.FC<PricingTableProps> = ({
  analyticsSurface,
  layoutMode = 'auto',
  onCancel,
  onPurchaseSuccess,
}) => {
  const { products, loading, error, refetch } = useLoonProducts();
  const {
    activePricingPriceId,
    baselinePricePerLoon,
    onSelectPricingPack,
    onCheckout,
    isCheckoutLoading,
    isCheckoutSuccess,
    isCheckoutEnabled,
    checkoutButtonLabel,
    checkoutError,
  } = useProductPricingTable({ products, analyticsSurface, onPurchaseSuccess });

  const forceCompact = layoutMode === 'singleColumn';

  if (loading) {
    return (
      <div className="relative flex min-h-[200px] items-center justify-center">
        <TransitionMessage messages={SITE.loadingProducts} showSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Empty className="min-h-[200px] border bg-card">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CircleAlertIcon />
          </EmptyMedia>
          <EmptyTitle>Oops! Something went wrong.</EmptyTitle>
          <EmptyDescription>{error}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button
            type="button"
            onClick={() => {
              void refetch();
            }}
          >
            Try again
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-foreground">
        <div>No pricing plans available at this time.</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-col items-center',
        !forceCompact && 'max-w-6xl px-4 md:px-8'
      )}
    >
      <RadioGroup
        value={activePricingPriceId ?? undefined}
        onValueChange={onSelectPricingPack}
        aria-label="Loon packs"
        className={cn(
          'grid w-full',
          forceCompact
            ? 'grid-cols-1 gap-0.5'
            : 'grid-cols-1 gap-0.5 lg:grid-cols-4 lg:gap-4'
        )}
      >
        {products.map((product) => (
          <PricingPackEntry
            key={product._id}
            view={buildPackView(product, baselinePricePerLoon)}
            forceCompact={forceCompact}
          />
        ))}
      </RadioGroup>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        *Extra Loons percentage shown as compared to price of 10 Loon pack
      </p>

      <div
        className={cn(
          'flex w-full flex-col items-center gap-3',
          forceCompact ? 'mt-4' : 'mt-8'
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col gap-2',
            !forceCompact && 'max-w-xs'
          )}
        >
          <Button
            type="button"
            size={forceCompact ? 'default' : 'xl'}
            className="w-full"
            disabled={!isCheckoutEnabled || isCheckoutSuccess}
            onClick={() => {
              void onCheckout();
            }}
          >
            {isCheckoutLoading ? (
              <Spinner className="size-4" />
            ) : (
              checkoutButtonLabel
            )}
          </Button>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              size={forceCompact ? 'default' : 'xl'}
              className="w-full"
              disabled={isCheckoutLoading}
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
        </div>
        {checkoutError && (
          <p className="text-sm text-destructive" role="alert">
            {checkoutError}
          </p>
        )}
      </div>
    </div>
  );
};

interface PricingPackEntryProps {
  view: PricingPackView;
  forceCompact: boolean;
}

/**
 * Wraps both compact (row) and wide (card) renderings in a single label so
 * keyboard + click selection work across breakpoints. Compact is always
 * rendered; wide is rendered only in auto mode and toggled by viewport.
 */
const PricingPackEntry: React.FC<PricingPackEntryProps> = ({
  view,
  forceCompact,
}) => (
  <label className="group relative block cursor-pointer">
    <RadioGroupItem value={view.stripePriceId} className="sr-only" />

    {/* Compact row — always rendered; hidden at lg+ unless forceCompact */}
    <CompactPricingItem
      view={view}
      className={forceCompact ? 'flex' : 'flex lg:hidden'}
    />

    {/* Wide card — only in auto mode at lg+ */}
    {!forceCompact && (
      <WidePricingCard view={view} className="hidden lg:flex" />
    )}
  </label>
);

interface PricingVariantProps {
  view: PricingPackView;
  className?: string;
}

const CompactPricingItem: React.FC<PricingVariantProps> = ({
  view,
  className,
}) => (
  <Item
    className={cn(
      view.gradientClasses,
      'min-h-16 ring-2 ring-transparent transition',
      'group-hover:ring-primary/50',
      'group-has-[button[data-state=checked]]:ring-primary',
      className
    )}
  >
    <RadioGroupIndicatorDot />
    <ItemMedia className="size-10">
      <img src={view.asset} alt="" className="h-full w-auto" />
    </ItemMedia>
    <ItemContent className="gap-0.5">
      <ItemTitle className="text-base font-bold text-white">
        {view.loonsLabel}
      </ItemTitle>
      {view.extraLoonsPercent !== null && (
        <Badge variant="outline" className="border-foreground/40">
          {view.extraLoonsPercent}% Extra
        </Badge>
      )}
    </ItemContent>
    <ItemActions>
      {view.pricingBadge && (
        <Badge className="px-2 py-0 text-[0.6rem] font-bold tracking-wider uppercase">
          {view.pricingBadge}
        </Badge>
      )}
      <span className="text-lg font-bold text-white">{view.price}</span>
    </ItemActions>
  </Item>
);

const WidePricingCard: React.FC<PricingVariantProps> = ({
  view,
  className,
}) => (
  <div
    className={cn(
      'relative flex-col items-center justify-between gap-3 rounded-2xl p-5 ring-2 ring-transparent transition',
      view.gradientClasses,
      'group-hover:ring-primary/50',
      'group-has-[button[data-state=checked]]:ring-primary',
      className
    )}
  >
    {view.pricingBadge && (
      <Badge className="absolute -top-2.5 left-1/2 z-10 -translate-x-1/2 px-3 py-0.5 text-[0.7rem] font-bold tracking-wider uppercase">
        {view.pricingBadge}
      </Badge>
    )}
    <div className="flex h-40 w-full items-center justify-center pt-2">
      <img src={view.asset} alt="" className="h-full w-auto" />
    </div>
    <div className="flex flex-col items-center gap-2">
      <div className="text-center text-2xl font-bold text-white">
        {view.loonsLabel}
      </div>
      <div className="flex min-h-6 items-center justify-center">
        {view.extraLoonsPercent !== null && (
          <Badge variant="outline" className="border-foreground/40">
            {view.extraLoonsPercent}% Extra
          </Badge>
        )}
      </div>
    </div>
    <div className="flex w-full flex-col items-center">
      <Separator className="mb-4 bg-white/15" />
      <div className="flex items-center justify-center gap-4">
        <RadioGroupIndicatorDot />
        <span className="text-xl font-bold text-white">{view.price}</span>
      </div>
    </div>
  </div>
);

/**
 * Visual-only selection dot. The real RadioGroupItem is sr-only on the
 * wrapping label — this dot mirrors its checked state via has-[] so the
 * entire label is clickable and the dot animates with selection.
 */
const RadioGroupIndicatorDot: React.FC = () => (
  <span
    aria-hidden
    className={cn(
      'inline-flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-white/50 bg-transparent transition',
      'group-has-[button[data-state=checked]]:border-primary group-has-[button[data-state=checked]]:bg-primary'
    )}
  >
    <span className="size-1.5 rounded-full bg-primary-foreground opacity-0 group-has-[button[data-state=checked]]:opacity-100" />
  </span>
);

export default PricingTable;
